import { useEffect, useMemo, useState } from "react";
import type { LocalStore } from "@onchainreps/storage";
import { DrillBoard } from "./DrillBoard";
import type { CourseCatalog, DrillItem, OpeningCourse } from "../lib/drills";
import { openingDrills } from "../lib/drills";
import { useDrillSession } from "../lib/useDrillSession";

interface OpeningsTrainerProps {
  store: LocalStore | null;
}

const COURSE_URL = `${import.meta.env.BASE_URL}courses/openings.json`;

export function OpeningsTrainer({ store }: OpeningsTrainerProps) {
  const [catalog, setCatalog] = useState<CourseCatalog | null>(null);
  const [color, setColor] = useState<"white" | "black" | "all">("all");
  const [family, setFamily] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [drills, setDrills] = useState<DrillItem[]>([]);
  const [course, setCourse] = useState<OpeningCourse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const session = useDrillSession(drills, store);

  useEffect(() => {
    let active = true;
    void fetch(COURSE_URL)
      .then((response) => {
        if (!response.ok) throw new Error("Could not load the opening courses.");
        return response.json() as Promise<CourseCatalog>;
      })
      .then((loaded) => {
        if (active) setCatalog(loaded);
      })
      .catch((cause: unknown) => {
        if (active) setError(cause instanceof Error ? cause.message : "Could not load courses.");
      });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    const needle = query.trim().toLowerCase();
    return catalog.openings.filter((opening) => {
      if (color !== "all" && opening.color !== color) return false;
      if (family !== "all" && opening.family !== family) return false;
      if (!needle) return true;
      return (
        opening.name.toLowerCase().includes(needle) ||
        opening.eco.toLowerCase().includes(needle) ||
        opening.family.toLowerCase().includes(needle)
      );
    });
  }, [catalog, color, family, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, OpeningCourse[]>();
    for (const opening of filtered) {
      const list = groups.get(opening.family) ?? [];
      list.push(opening);
      groups.set(opening.family, list);
    }
    return [...groups.entries()].slice(0, 40);
  }, [filtered]);

  const start = (opening: OpeningCourse) => {
    const items = openingDrills(opening);
    if (items.length === 0) {
      setError("That line could not be replayed.");
      return;
    }
    setCourse(opening);
    setDrills(items);
    setError(null);
  };

  if (course && session.current) {
    return (
      <div className="trainer">
        <div className="trainer__bar">
          <button className="btn btn--inline" onClick={() => setCourse(null)}>
            Back to courses
          </button>
          <span className="trainer__count">
            {session.index + 1} / {session.total}
          </span>
          <span className="trainer__streak">
            Streak {session.streak} · Best {session.bestStreak}
          </span>
        </div>

        <div className="progress" aria-hidden="true">
          <div
            className="progress__fill"
            style={{ width: `${session.total === 0 ? 0 : (session.index / session.total) * 100}%` }}
          />
        </div>

        <DrillBoard
          key={session.current.id}
          drill={session.current}
          onAnswer={session.answer}
          onNext={() => {
            const last = session.index + 1 >= session.total;
            session.next();
            if (last) setCourse(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="trainer">
      <section className="panel">
        <div className="panel__head">
          <h1 className="panel__title">Opening courses</h1>
          <span className="chip">
            <span className="chip__dot" />
            {catalog ? `${catalog.openings.length} lines` : "Loading"}
          </span>
        </div>
        <div className="panel__body">
          <p className="notice">
            Lines come from the Lichess opening collection, released under CC0. Drill the moves you
            actually play, and the scheduler brings the ones you forget back.
          </p>

          <div className="field">
            <label className="field__label" htmlFor="course-search">
              Search
            </label>
            <input
              id="course-search"
              className="input"
              type="search"
              placeholder="Sicilian, B12, King’s Indian"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="field">
            <span className="field__label">Colour</span>
            <div className="segmented">
              {(
                [
                  ["all", "All"],
                  ["white", "White"],
                  ["black", "Black"],
                ] as Array<["all" | "white" | "black", string]>
              ).map(([id, label]) => (
                <button
                  key={id}
                  role="tab"
                  aria-selected={color === id}
                  onClick={() => setColor(id)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="course-family">
              Opening family
            </label>
            <select
              id="course-family"
              className="input"
              value={family}
              onChange={(event) => setFamily(event.target.value)}
            >
              <option value="all">All families</option>
              {(catalog?.families ?? []).map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {error ? <p className="notice notice--error">{error}</p> : null}

          <div className="courselist">
            {grouped.map(([name, openings]) => (
              <details className="coursegroup" key={name} open={grouped.length <= 3}>
                <summary>
                  <span>{name}</span>
                  <span className="coursegroup__count">{openings.length}</span>
                </summary>
                <ul>
                  {openings.slice(0, 40).map((opening) => (
                    <li key={opening.slug}>
                      <button className="courselink" onClick={() => start(opening)}>
                        <span className="courselink__eco">{opening.eco}</span>
                        <span className="courselink__name">{opening.name}</span>
                        <span className="courselink__color">{opening.color === "white" ? "W" : "B"}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            ))}
            {catalog && grouped.length === 0 ? (
              <p className="notice">No course matches that search yet.</p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
